import React, { useEffect } from "react";
import {
  ArrayInput,
  BooleanInput,
  DateInput,
  NumberInput,
  SelectInput,
  SimpleFormIterator,
  TimeInput,
} from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import PercentIcon from "@mui/icons-material/Percent";
import { STOCK_TYPES } from "../enums/StockTypes";
import { STOCK_AVAILABILITY_MODES } from "../enums/StockAvailabilityModes";
import {
  STOCK_TYPES_OPTIONS,
  STOCK_AVAILABILITY_MODES_OPTIONS,
} from "../utils/ProductUtils";
import {
  MAX_MORFY_SPLIT_PERCENTAGE_INPUT,
  validateCustomSplitPercentage,
  validateCustomSplitUnits,
  validateQuantityOptional,
  validateRequired,
  validateStockDays,
  validateStockDaysRange,
  validateStockWithZero,
} from "../utils/Validations";
import {
  fractionToPercentInput,
  hasValue,
  percentInputToFraction,
} from "../utils/NumberUtils";
import {
  eachUtcDayInRange,
  formatTimeTimezone,
  formatUtcDateInput,
  parseTimeTimezone,
  parseUtcDateInput,
} from "../utils/DateTimeUtils";

const DAYS_SOURCE = "stockAvailability.days";
const RANGE_FROM_SOURCE = "stockDaysFrom";
const RANGE_TO_SOURCE = "stockDaysTo";
const BULK_QUANTITY_SOURCE = "stockDaysBulkQuantity";
const BULK_SPLIT_UNITS_SOURCE = "stockDaysBulkSplitUnits";
const BULK_SPLIT_PERCENTAGE_SOURCE = "stockDaysBulkSplitPercentage";

// Every input inside a section is laid out on a fixed grid so labels and
// helper texts line up across rows instead of collapsing to their own widths.
const FIELD_WIDTH = 200;
const NARROW_FIELD_WIDTH = 160;

const FORM_MAX_WIDTH = 960;

/**
 * A titled block of related inputs. Grouping the stock inputs this way keeps
 * the tab readable: the mode selector, the day generator, the bulk actions and
 * the per-day list each read as one step instead of a single long field list.
 */
const Section = ({ title, description, action, children }) => (
  <Paper variant="outlined" sx={{ p: 2.5, width: "100%" }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Box>
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {description}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
    <Box sx={{ mt: 1.5 }}>{children}</Box>
  </Paper>
);

const InputRow = ({ children }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      flexWrap: "wrap",
      gap: 2,
    }}
  >
    {children}
  </Box>
);

// Buttons sit next to `margin="dense"` inputs, so they need the same nudge to
// align with the input box rather than with the floating label.
const rowButtonSx = { mt: 1, flexShrink: 0 };

const StockDaysGenerator = () => {
  const { setValue } = useFormContext();

  const from = useWatch({ name: RANGE_FROM_SOURCE });
  const to = useWatch({ name: RANGE_TO_SOURCE });
  const bulkQuantity = useWatch({ name: BULK_QUANTITY_SOURCE });
  const bulkSplitUnits = useWatch({ name: BULK_SPLIT_UNITS_SOURCE });
  const bulkSplitPercentage = useWatch({ name: BULK_SPLIT_PERCENTAGE_SOURCE });
  const days = useWatch({ name: DAYS_SOURCE });

  const rangeDates = eachUtcDayInRange(from, to);
  const hasDays = Boolean(days?.length);

  const hasBulkSplitValues =
    hasValue(bulkSplitUnits) && hasValue(bulkSplitPercentage);
  const bulkSplitPercentageValue = percentInputToFraction(bulkSplitPercentage);
  const isBulkSplitInRange =
    Number(bulkSplitUnits) >= 0 &&
    Number.isInteger(Number(bulkSplitUnits)) &&
    Number(bulkSplitPercentage) >= 0 &&
    Number(bulkSplitPercentage) < MAX_MORFY_SPLIT_PERCENTAGE_INPUT;
  const canApplyBulkSplit = hasBulkSplitValues && isBulkSplitInRange;

  useEffect(() => {
    if (from || to || !days?.length) return;

    setValue(RANGE_FROM_SOURCE, formatUtcDateInput(days[0].date));
    setValue(RANGE_TO_SOURCE, formatUtcDateInput(days[days.length - 1].date));
  }, [days, from, to, setValue]);

  const generateDays = () => {
    if (!rangeDates.length) return;

    const dayByDate = new Map(
      (days ?? []).map((day) => [formatUtcDateInput(day.date), day])
    );

    setValue(
      DAYS_SOURCE,
      rangeDates.map((date) => {
        const existing = dayByDate.get(formatUtcDateInput(date));

        return {
          date: date.toISOString(),
          quantity: existing?.quantity ?? 0,
          ...(existing?.customMorfySplit && {
            customMorfySplit: existing.customMorfySplit,
          }),
        };
      }),
      { shouldDirty: true }
    );
  };

  const applyBulkQuantity = () => {
    if (!days?.length || !hasValue(bulkQuantity)) return;

    setValue(
      DAYS_SOURCE,
      days.map((day) => ({ ...day, quantity: Number(bulkQuantity) })),
      { shouldDirty: true }
    );
  };

  const applyBulkCustomSplit = () => {
    if (!days?.length || !canApplyBulkSplit) return;

    setValue(
      DAYS_SOURCE,
      days.map((day) => {
        const dayQuantity = hasValue(day.quantity) ? Number(day.quantity) : 0;

        return {
          ...day,
          customMorfySplit: {
            quantity: Math.min(Number(bulkSplitUnits), dayQuantity),
            percentage: bulkSplitPercentageValue,
          },
        };
      }),
      { shouldDirty: true }
    );
  };

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <Section
        title="Rango de fechas"
        description="Elegí el período del evento y generá un día por fecha."
        action={
          rangeDates.length > 0 && (
            <Chip
              size="small"
              label={`${rangeDates.length} ${
                rangeDates.length === 1 ? "día" : "días"
              } en el rango`}
            />
          )
        }
      >
        <InputRow>
          <DateInput
            source={RANGE_FROM_SOURCE}
            label="Desde"
            format={formatUtcDateInput}
            parse={parseUtcDateInput}
            helperText={false}
            sx={{ width: FIELD_WIDTH }}
          />
          <DateInput
            source={RANGE_TO_SOURCE}
            label="Hasta"
            format={formatUtcDateInput}
            parse={parseUtcDateInput}
            validate={validateStockDaysRange}
            helperText={false}
            sx={{ width: FIELD_WIDTH }}
          />
          <Button
            variant="contained"
            disableElevation
            startIcon={<EventRepeatIcon />}
            onClick={generateDays}
            disabled={!rangeDates.length}
            sx={rowButtonSx}
          >
            Generar días
          </Button>
        </InputRow>

        {hasDays && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            Al regenerar se conservan la cantidad y la comisión de las fechas
            que sigan dentro del rango.
          </Typography>
        )}
      </Section>

      <Section
        title="Aplicar a todos los días"
        description={
          hasDays
            ? "Atajos para completar la lista de abajo de una sola vez."
            : "Disponible una vez que generes los días."
        }
      >
        <Stack spacing={2} divider={<Divider flexItem />}>
          <InputRow>
            <NumberInput
              source={BULK_QUANTITY_SOURCE}
              label="Cantidad"
              min={0}
              disabled={!hasDays}
              onWheel={(e) => e.target.blur()}
              helperText={false}
              sx={{ width: FIELD_WIDTH }}
            />
            <Button
              variant="outlined"
              startIcon={<DoneAllIcon />}
              onClick={applyBulkQuantity}
              disabled={!hasDays || !hasValue(bulkQuantity)}
              sx={rowButtonSx}
            >
              Aplicar cantidad
            </Button>
          </InputRow>

          <Box>
            <InputRow>
              <NumberInput
                source={BULK_SPLIT_UNITS_SOURCE}
                label="Primeras unidades"
                min={0}
                disabled={!hasDays}
                onWheel={(e) => e.target.blur()}
                helperText={false}
                sx={{ width: FIELD_WIDTH }}
              />
              <NumberInput
                source={BULK_SPLIT_PERCENTAGE_SOURCE}
                label="Comisión %"
                min={0}
                max={MAX_MORFY_SPLIT_PERCENTAGE_INPUT}
                disabled={!hasDays}
                onWheel={(e) => e.target.blur()}
                helperText={false}
                sx={{ width: NARROW_FIELD_WIDTH }}
              />
              <Button
                variant="outlined"
                startIcon={<PercentIcon />}
                onClick={applyBulkCustomSplit}
                disabled={!hasDays || !canApplyBulkSplit}
                sx={rowButtonSx}
              >
                Aplicar comisión
              </Button>
            </InputRow>

            {hasBulkSplitValues && !isBulkSplitInRange ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {`Las primeras unidades deben ser un entero mayor o igual a 0, y la comisión menor a ${MAX_MORFY_SPLIT_PERCENTAGE_INPUT}%`}
              </Alert>
            ) : (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1 }}
              >
                Las primeras unidades de cada día se cobran con esta comisión.
                Luego, se usará la comisión definida en Datos Básicos.
              </Typography>
            )}
          </Box>
        </Stack>
      </Section>
    </Stack>
  );
};

const StockDaysSummary = ({ days }) => {
  const totalUnits = (days ?? []).reduce(
    (total, day) => total + (Number(day?.quantity) || 0),
    0
  );

  return (
    <Stack direction="row" spacing={1}>
      <Chip
        size="small"
        label={`${days.length} ${days.length === 1 ? "día" : "días"}`}
      />
      <Chip size="small" label={`${totalUnits} unidades en total`} />
    </Stack>
  );
};

const PerDayStockInputs = () => {
  const days = useWatch({ name: DAYS_SOURCE });
  const hasDays = Boolean(days?.length);

  return (
    <>
      <StockDaysGenerator />

      <Section
        title="Stock por día"
        description="Cantidad disponible en cada fecha. Opcionalmente, una comisión distinta para las primeras unidades del día."
        action={hasDays && <StockDaysSummary days={days} />}
      >
        {!hasDays && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Todavía no hay días cargados. Generalos desde el rango de fechas o
            agregalos de a uno.
          </Alert>
        )}

        <ArrayInput
          source={DAYS_SOURCE}
          label={false}
          validate={validateStockDays}
          helperText={false}
        >
          <SimpleFormIterator
            inline
            disableReordering
            getItemLabel={(index) => `${index + 1}.`}
            sx={{
              "& .RaSimpleFormIterator-line": { py: 1 },
              "& .RaSimpleFormIterator-index": {
                alignSelf: "center",
                color: "text.secondary",
              },
            }}
          >
            <DateInput
              source="date"
              label="Fecha"
              format={formatUtcDateInput}
              parse={parseUtcDateInput}
              validate={validateRequired}
              helperText={false}
              sx={{ width: NARROW_FIELD_WIDTH }}
            />
            <NumberInput
              source="quantity"
              label="Cantidad"
              min={0}
              validate={validateStockWithZero}
              onWheel={(e) => e.target.blur()}
              helperText={false}
              sx={{ width: NARROW_FIELD_WIDTH }}
            />
            <NumberInput
              source="customMorfySplit.quantity"
              label="Primeras unidades"
              min={0}
              validate={validateCustomSplitUnits}
              onWheel={(e) => e.target.blur()}
              helperText={false}
              sx={{ width: NARROW_FIELD_WIDTH }}
            />
            <NumberInput
              source="customMorfySplit.percentage"
              label="Comisión %"
              min={0}
              max={MAX_MORFY_SPLIT_PERCENTAGE_INPUT}
              format={fractionToPercentInput}
              parse={percentInputToFraction}
              validate={validateCustomSplitPercentage}
              onWheel={(e) => e.target.blur()}
              helperText={false}
              sx={{ width: NARROW_FIELD_WIDTH }}
            />
          </SimpleFormIterator>
        </ArrayInput>
      </Section>
    </>
  );
};

const RenewingStockInputs = ({ country, renewDaily }) => (
  <>
    <Section
      title="Vigencia"
      description="Período en el que el producto tiene stock disponible."
    >
      <InputRow>
        <DateInput
          source="stockAvailability.dateRange.from"
          validate={validateRequired}
          label="Stock desde"
          helperText={false}
          sx={{ width: FIELD_WIDTH }}
        />
        <DateInput
          source="stockAvailability.dateRange.to"
          validate={validateRequired}
          label="Stock hasta"
          helperText={false}
          sx={{ width: FIELD_WIDTH }}
        />
      </InputRow>
    </Section>

    <Section
      title="Stock diario"
      description="Cantidad que se habilita cada día y a qué hora vuelve a estar disponible."
    >
      <InputRow>
        <NumberInput
          source="stockAvailability.dailyDefaults.quantity"
          label="Cantidad diaria"
          min={1}
          validate={validateRequired}
          onWheel={(e) => e.target.blur()}
          helperText={false}
          sx={{ width: FIELD_WIDTH }}
        />
        <TimeInput
          source="stockAvailability.dailyDefaults.restartHour"
          label="Horario de inicio de stock"
          validate={validateRequired}
          variant="outlined"
          margin="dense"
          format={formatTimeTimezone(country)}
          parse={parseTimeTimezone(country, true)}
          helperText={false}
          sx={{ width: FIELD_WIDTH }}
        />
        <BooleanInput
          source="stockAvailability.renewDaily"
          label="Renovar stock"
          variant="outlined"
          margin="dense"
          defaultValue={true}
          helperText={false}
          sx={{ mt: 1 }}
        />
      </InputRow>
    </Section>

    {renewDaily && (
      <Section
        title="Ajustes de stock"
        description="Excepciones para fechas puntuales, por encima del stock diario."
      >
        <ArrayInput
          source="stockAvailability.overrides"
          label={false}
          helperText={false}
        >
          <SimpleFormIterator
            inline
            disableReordering
            sx={{ "& .RaSimpleFormIterator-line": { py: 1 } }}
          >
            <DateInput
              source="date"
              label="Fecha"
              validate={validateRequired}
              helperText={false}
              sx={{ width: NARROW_FIELD_WIDTH }}
            />
            <NumberInput
              source="quantity"
              label="Cantidad"
              min={0}
              validate={validateStockWithZero}
              onWheel={(e) => e.target.blur()}
              helperText={false}
              sx={{ width: NARROW_FIELD_WIDTH }}
            />
            <TimeInput
              source="restartHour"
              label="Horario de inicio"
              validate={validateRequired}
              format={formatTimeTimezone(country)}
              parse={parseTimeTimezone(country, true)}
              variant="outlined"
              margin="dense"
              helperText={false}
              sx={{ width: NARROW_FIELD_WIDTH }}
            />
          </SimpleFormIterator>
        </ArrayInput>
      </Section>
    )}
  </>
);

/**
 * The Stock tab body, shared by ProductCreate and ProductEdit.
 *
 * The two availability modes are mutually exclusive and the API rejects a
 * payload carrying keys from both, so transformProduct rebuilds stockAvailability
 * from the selected mode before saving — hiding an input is not enough, since
 * react-hook-form keeps the values of unmounted fields.
 */
const ProductStockTab = ({ formData }) => {
  // On create there is no restaurantData yet, only a restaurant id, so the
  // restart hour is interpreted as UTC. Pre-existing behaviour, left alone here.
  const country = formData?.restaurantData?.country;
  const isLimited = formData?.stockType === STOCK_TYPES.LIMITED;
  const isPerDay =
    formData?.stockAvailability?.mode === STOCK_AVAILABILITY_MODES.PER_DAY;

  return (
    <Stack spacing={2} sx={{ width: "100%", maxWidth: FORM_MAX_WIDTH }}>
      <Section
        title="Disponibilidad"
        description="Definí si el producto tiene stock limitado y cómo se administra."
      >
        <InputRow>
          <SelectInput
            label="Tipo"
            source="stockType"
            choices={STOCK_TYPES_OPTIONS}
            validate={validateRequired}
            defaultValue={STOCK_TYPES.UNLIMITED}
            helperText={false}
            sx={{ width: FIELD_WIDTH }}
          />
          {isLimited && (
            <SelectInput
              label="Modo de stock"
              source="stockAvailability.mode"
              choices={STOCK_AVAILABILITY_MODES_OPTIONS}
              validate={validateRequired}
              defaultValue={STOCK_AVAILABILITY_MODES.RENEWING}
              helperText={false}
              sx={{ width: FIELD_WIDTH }}
            />
          )}
        </InputRow>
      </Section>

      {isLimited && (
        <>
          {isPerDay ? (
            <PerDayStockInputs />
          ) : (
            <RenewingStockInputs
              country={country}
              renewDaily={formData?.stockAvailability?.renewDaily}
            />
          )}

          <Section title="Límite por usuario">
            <NumberInput
              source="maxUserStock"
              label="Máximo por usuario por día"
              min={1}
              validate={validateQuantityOptional}
              onWheel={(e) => e.target.blur()}
              helperText={false}
              sx={{ width: 260 }}
            />
          </Section>
        </>
      )}
    </Stack>
  );
};

export default ProductStockTab;
